import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { useApi } from '../hooks/useApi';
import { useSound } from '../hooks/useSound';

interface ScanResult {
  type: 'product' | 'location' | 'order' | 'unknown';
  code: string;
  data?: {
    id: string;
    title?: string;
    sku?: string;
    locationCode?: string;
    orderNumber?: string;
  };
}

export default function Scanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi } = useApi();
  const { playSuccess, playError, playBeep } = useSound();
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);

  const [scanning, setScanning] = useState(true);
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState('');

  const mode = searchParams.get('mode') || 'lookup'; // lookup, pick, stocktake
  const pickListId = searchParams.get('pickList');
  const stockTakeId = searchParams.get('stockTake');

  useEffect(() => {
    startScanning();

    return () => {
      stopScanning();
    };
  }, []);

  async function startScanning() {
    try {
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;

      const videoInputDevices = await reader.listVideoInputDevices();

      // Prefer back camera
      const backCamera = videoInputDevices.find(
        (device) =>
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('rear')
      );

      const deviceId = backCamera?.deviceId || videoInputDevices[0]?.deviceId;

      if (deviceId && videoRef.current) {
        reader.decodeFromVideoDevice(
          deviceId,
          videoRef.current,
          (result, err) => {
            if (result) {
              handleScan(result.getText());
            }
            if (err && !(err instanceof NotFoundException)) {
              console.error('Scan error:', err);
            }
          }
        );
        setScanning(true);
      }
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setError('Camera access denied. Please grant permission or enter code manually.');
      setScanning(false);
    }
  }

  function stopScanning() {
    if (readerRef.current) {
      readerRef.current.reset();
      readerRef.current = null;
    }
    setScanning(false);
  }

  async function handleScan(code: string) {
    // Pause scanning while processing
    stopScanning();

    // Play beep to acknowledge scan
    playBeep();

    // Vibrate for feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    // Look up the code
    const result = await fetchApi<ScanResult>(`/warehouse/lookup?code=${encodeURIComponent(code)}${pickListId ? `&pick_list_id=${encodeURIComponent(pickListId)}` : ''}`);

    if (result?.type) {
      // Play success sound for recognized codes
      playSuccess();
      setLastScan(result);

      // Handle different modes
      if (mode === 'pick' && pickListId && result.type === 'product') {
        // Auto-navigate to pick item
        navigate(`/pick/${pickListId}/item/${result.data?.id}`);
        return;
      }

      if (mode === 'stocktake' && stockTakeId && result.type === 'location') {
        // Handle location scan in stock take
        navigate(`/stock-take/${stockTakeId}?location=${result.data?.locationCode}`);
        return;
      }
    } else {
      // Play error sound for unrecognized codes
      playError();
      setLastScan({
        type: 'unknown',
        code,
      });
    }
  }

  const handleManualEntry = () => {
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
    }
  };

  const handleContinueScanning = () => {
    setLastScan(null);
    startScanning();
  };

  return (
    <div className="page" style={{ background: '#000' }}>
      {/* Camera View */}
      {scanning && (
        <div className="camera-container">
          <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

          <div className="camera-overlay">
            <div className="scan-frame" />
            <p style={{ color: 'white', marginTop: '1rem' }}>
              Position barcode within frame
            </p>
          </div>

          <button className="camera-close" onClick={() => navigate(-1)}>
            ✕
          </button>
        </div>
      )}

      {/* Error or Manual Entry */}
      {!scanning && !lastScan && (
        <div className="page-content" style={{ background: 'white', minHeight: '100vh' }}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            {error && (
              <div
                style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '1rem',
                  borderRadius: 'var(--radius)',
                  marginBottom: '1rem',
                }}
              >
                {error}
              </div>
            )}

            <h2 style={{ marginBottom: '1rem' }}>Enter Code Manually</h2>

            <div className="form-group">
              <input
                type="text"
                className="form-input"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Enter barcode or SKU"
                autoFocus
              />
            </div>

            <button className="btn btn-primary btn-large" onClick={handleManualEntry}>
              Look Up
            </button>

            <button
              className="btn btn-secondary btn-large"
              style={{ marginTop: '0.5rem' }}
              onClick={startScanning}
            >
              Try Camera Again
            </button>
          </div>
        </div>
      )}

      {/* Scan Result */}
      {lastScan && (
        <div className="page-content" style={{ background: 'white', minHeight: '100vh' }}>
          <div style={{ padding: '2rem' }}>
            {lastScan.type === 'unknown' ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❓</div>
                <h2>Unknown Code</h2>
                <p style={{ color: 'var(--gray-500)', marginBottom: '1rem' }}>
                  "{lastScan.code}" not found in the system
                </p>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '4rem',
                    marginBottom: '1rem',
                  }}
                >
                  {lastScan.type === 'product' ? '📦' : lastScan.type === 'location' ? '📍' : '📋'}
                </div>

                <div className="card">
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--gray-500)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {lastScan.type}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {lastScan.data?.sku || lastScan.data?.locationCode || lastScan.data?.orderNumber}
                    </div>
                    {lastScan.data?.title && (
                      <div style={{ color: 'var(--gray-500)' }}>{lastScan.data.title}</div>
                    )}
                  </div>
                </div>

                {/* Action Buttons based on type */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {lastScan.type === 'product' && (
                    <>
                      <button
                        className="btn btn-primary btn-large"
                        onClick={() => navigate(`/inventory?sku=${lastScan.data?.sku}`)}
                      >
                        View Inventory
                      </button>
                      <button className="btn btn-secondary btn-large" onClick={() => navigate('/stock-enrollment/'+lastScan.data?.id)}>Stock tracking setup</button>
                      <button className="btn btn-secondary btn-large" onClick={() => navigate('/opening-stock/'+lastScan.data?.id)}>Record opening stock</button>
                      {pickListId && (
                        <button
                          className="btn btn-success btn-large"
                          onClick={() => navigate(`/pick/${pickListId}/item/${lastScan.data?.id}`)}
                        >
                          Pick This Item
                        </button>
                      )}
                    </>
                  )}

                  {lastScan.type === 'location' && (
                    <button
                      className="btn btn-primary btn-large"
                      onClick={() => navigate(`/inventory?location=${lastScan.data?.locationCode}`)}
                    >
                      View Location Stock
                    </button>
                  )}

                  {lastScan.type === 'order' && (
                    <button
                      className="btn btn-primary btn-large"
                      onClick={() => navigate(`/orders/${lastScan.data?.id}`)}
                    >
                      View Order
                    </button>
                  )}
                </div>
              </div>
            )}

            <button
              className="btn btn-secondary btn-large"
              style={{ marginTop: '1rem' }}
              onClick={handleContinueScanning}
            >
              Scan Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
